import { SortDirectionType, SortingOptionsType } from '@components/design/migrated/Table';
import { ReactElement } from 'react';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { ProductsLineChartResp } from '@components/products-line-charts/Types';
import { KpiOptionForLabel, KpiOptionForValue, KpiTypesOption } from '@components/products-top-5-table/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { ShareComparisonChartResp } from '@data-visualization/share-comparison-chart/Types';
import { Filters, Option } from '@utils/pageConfig/Types';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { BaseTimePeriod } from '@services/FiltersTypes';

export interface ProductsPage {
  productGroup: string;
  country?: Country;
  content: LeaderboardKpi[];
  pageable: Pageable;
  salesRecordsFound: boolean;
  groupingKpi?: ProductAnalyzerGroupKpi[];
}

export interface LeaderboardKpi {
  item: Item;
  marketRank: Rank;
  marketRevenueRank: Rank;
  marketVolumeRank: Rank;
  marketRevenue: Kpi;
  marketVolume: Kpi;
  marketPrice: Kpi;
  marketShareRevenue: KpiPercentage;
  marketShareVolume: KpiPercentage;
  marketShareRatio?: number;
  marketShareValue?: number;
  retailerRank: Rank;
  retailerRevenueRank: Rank;
  retailerVolumeRank: Rank;
  retailerRevenue: Kpi;
  retailerVolume: Kpi;
  retailerPrice: Kpi;
  marketExcludeRetailRevenue: Kpi;
  marketExcludeRetailVolume: Kpi;
  marketExcludeRetailPrice: Kpi;
  retailerShareRevenue: KpiPercentage;
  retailerShareVolume: KpiPercentage;
  retailerMixRevenue: KpiPercentage;
  retailerMixVolume: KpiPercentage;
  rollingSalesDto?: ProductsLineChartResp;
  features?: Feature[];
  grouping: string;
}

export interface ProductsParam {
  isUsingCollectionLiveData?: boolean;
  clientId: number;
  productType: ProductType;
  countryId: string;
  productGroup: Option | undefined;
  periodType: Periodicity;
  period: string;
  comparisonPeriod: string;
  pageRequest: PageRequest;
  sortOption: SortingOptionsType;
  appliedFilters: Filters[];
  kpi: SaleType;
  sortingKpi: string | undefined;
  isMarket: boolean;
  growthType: GrowthTypes | undefined;
  itemIdList?: string[];
  exportCompareComparisonType?: RelativePeriod | BaseTimePeriod;
  groupDetails?: ProductAnalyzerGroupDetails[];
  sortBy: string;
  sortDirection: string;
  sortKey: string | undefined;
}

export interface TopSellingExportParam {
  clientId: number;
  productType: ProductType;
  countryId: string;
  productGroup: Option | undefined;
  periodType: Periodicity;
  period: string;
  comparisonPeriod: string;
  isMarket: boolean;
  appliedFilters: Filters[];
  sortBy: string | undefined;
  sortDirection: string | undefined;
  sortKey: string | undefined;
  groupDetails?: ProductAnalyzerGroupDetails[];
}

export interface PageRequest {
  page: number;
  size: number;
}

export interface Features {
  name: string;
  value: string[];
}

export interface ProductsReq {
  clientId: number;
  productType: ProductType;
  productDataType: ProductDataType;
  productGroup: string;
  country: string[];
  period: string;
  periodType: string;
  comparisonPeriod: string;
  comparisonPeriodType: string;
  minPrice: string;
  maxPrice: string;
  distType: string[];
  channel: string[];
  region: string[];
  city: string[];
  brand: string[];
  features: Features[];
  startDate: string;
  endDate: string;
  kpi: string;
  calculateRollingPeriod: boolean;
  itemIdList?: string[];
  //Pagination use
  page: number;
  size: number;
  //Sorting use
  isMarket: boolean;
  sortBy: string | undefined;
  sortDirection: string | undefined;
  sortKey: string | undefined;
  comparisonGrowth: string | undefined;
  productGrouping?: ProductAnalyzerGroupDetails[];
  targetCurrency?: string;
}

interface Pageable {
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPage: number;
}

interface Country {
  countryCode: string;
  countryName: string;
  currency: Currency;
  stCountryId: number;
}

interface Currency {
  currencyCode: string;
  currencyName: string;
  stCurrencyId: number;
}

export interface Kpi {
  value: number;
  percentage: number;
  actual: number;
}

export interface Rank {
  rank: number;
  change: number;
  position: string;
}

export interface Item {
  itemId: string;
  itemName: string;
  itemBrand: string;
  firstActivityDate: string;
}

export interface Feature {
  name: string | null;
  value: string | null;
}

export interface KpiPercentage {
  growthPercentage: number;
  sharePercentage: number;
}

export enum ColumnHeader {
  checkbox = 'checkbox',
  itemName = 'itemName',
  marketRevenue = 'marketRevenue',
  marketShareRevenue = 'marketShareRevenue',
  marketShareVolume = 'marketShareVolume',
  marketVolume = 'marketVolume',
  marketPrice = 'marketPrice',
  marketRank = 'marketRank',
  marketShare = 'marketShare',
  retailerRevenue = 'retailerRevenue',
  retailerVolume = 'retailerVolume',
  retailerShareRevenue = 'retailerShareRevenue',
  retailerShareVolume = 'retailerShareVolume',
  retailerMixRevenue = 'retailerMixRevenue',
  retailerMixVolume = 'retailerMixVolume',
  retailerPrice = 'retailerPrice',
  retailerRank = 'retailerRank',
  firstActivity = 'firstActivity',
  feature = 'feature',
  ranking = 'ranking',
  brand = 'brand',
  marketRevenueRank = 'marketRevenueRank',
  marketRevenueRankChange = 'marketRevenueRankChange',
  marketRevenueChange = 'marketRevenueChange',
  marketVolumeRank = 'marketVolumeRank',
  marketVolumeRankChange = 'marketVolumeRankChange',
  marketVolumeChange = 'marketVolumeChange',
  marketPriceChange = 'marketPriceChange',
  retailerRevenueRank = 'retailerRevenueRank',
  retailerRevenueRankChange = 'retailerRevenueRankChange',
  retailerRevenueChange = 'retailerRevenueChange',
  retailerVolumeRank = 'retailerVolumeRank',
  retailerVolumeRankChange = 'retailerVolumeRankChange',
  retailerVolumeChange = 'retailerVolumeChange',
  retailerPriceChange = 'retailerPriceChange',
  marketExcludeRetailRevenue = 'marketExcludeRetailRevenue',
  marketExcludeRetailVolume = 'marketExcludeRetailVolume',
  marketExcludeRetailPrice = 'marketExcludeRetailPrice',
}

export enum SaleType {
  revenue = 'revenue',
  volume = 'volume',
  price = 'price',
}

export const getSaleTypeTrackingValue = (volume: string = DEFAULT_VOLUME): Record<SaleType, string> => ({
  [SaleType.revenue]: 'Revenue',
  [SaleType.volume]: 'Units',
  [SaleType.price]: 'Price',
});

export enum SortKeys {
  Absolute = '',
  Change = 'Change',
}

export enum SortLabels {
  Absolute = 'Absolute',
  Change = 'Change',
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum SortKeyLabels {
  ABSOLUTE = 'ABSOLUTE',
  CHANGE = 'CHANGE',
}

export enum PercentageKpi {
  retailerShareRevenue = 'retailerShareRevenue',
  retailerShareVolume = 'retailerShareVolume',
  retailerMixRevenue = 'retailerMixRevenue',
  retailerMixVolume = 'retailerMixVolume',
  marketShareRevenue = 'marketShareRevenue',
  marketShareVolume = 'marketShareVolume',
}

export const ColumnHeaderDisplayName = {
  [ColumnHeader.checkbox]: 'CheckBox',
  [ColumnHeader.itemName]: 'Model',
  [ColumnHeader.marketRank]: 'Market Ranking',
  [ColumnHeader.marketShareRevenue]: 'Market Revenue Share',
  [ColumnHeader.marketShareVolume]: 'Market Units Share',
  [ColumnHeader.marketRevenue]: 'Market Revenue',
  [ColumnHeader.marketVolume]: 'Market Units',
  [ColumnHeader.marketPrice]: 'Market Price',
  [ColumnHeader.marketShare]: 'Market Share',
  [ColumnHeader.retailerRank]: 'Retailer Ranking',
  [ColumnHeader.retailerRevenue]: 'Retailer Revenue',
  [ColumnHeader.retailerVolume]: 'Retailer Units',
  [ColumnHeader.retailerShareRevenue]: 'Retailer Revenue Share',
  [ColumnHeader.retailerShareVolume]: 'Retailer Units Share',
  [ColumnHeader.retailerMixRevenue]: 'Retailer Revenue Mix',
  [ColumnHeader.retailerMixVolume]: 'Retailer Units Mix',
  [ColumnHeader.retailerPrice]: 'Retailer Price',
  [ColumnHeader.firstActivity]: 'First Activity',
  [ColumnHeader.feature]: 'Feature',
  [ColumnHeader.ranking]: 'Ranking',
  [ColumnHeader.brand]: 'Brand',
  [ColumnHeader.marketRevenueRank]: 'Market Ranking (Revenue)',
  [ColumnHeader.marketRevenueRankChange]: 'Market Ranking Change (Revenue)',
  [ColumnHeader.marketRevenueChange]: 'Market Revenue Change',
  [ColumnHeader.marketVolumeRank]: 'Market Ranking (Units)',
  [ColumnHeader.marketVolumeRankChange]: 'Market Ranking Change (Units)',
  [ColumnHeader.marketVolumeChange]: 'Market Units Change',
  [ColumnHeader.marketPriceChange]: 'Market Price Change',
  [ColumnHeader.retailerRevenueRank]: 'Retailer Ranking (Revenue)',
  [ColumnHeader.retailerRevenueRankChange]: 'Retailer Ranking Change (Revenue)',
  [ColumnHeader.retailerRevenueChange]: 'Retailer Revenue Change',
  [ColumnHeader.retailerVolumeRank]: 'Retailer Ranking (Units)',
  [ColumnHeader.retailerVolumeRankChange]: 'Retailer Ranking Change (Units)',
  [ColumnHeader.retailerVolumeChange]: 'Retailer Units Change',
  [ColumnHeader.retailerPriceChange]: 'Retailer Price Change',
  [ColumnHeader.marketExcludeRetailRevenue]: 'Market excl. Retail Revenue',
  [ColumnHeader.marketExcludeRetailVolume]: 'Market excl. Retailer Units',
  [ColumnHeader.marketExcludeRetailPrice]: 'Market excl. Retailer Price',
};

export const ItemModel = 'itemName';
export const ModelGroup = 'modelGroup';

export const TopSellingProductsTableId = 'top-selling-products';
export const TopSellingProductsChartId = 'top-selling-products-chart';
export const CompareModelsTableId = 'compare-models';
export const volumeString = 'Volume';
export const revenueString = 'Revenue';
export const market = 'Market';
export const retailer = 'Retailer';

export const DefaultSortingOptionsForProducts: SortingOptionsType = {
  headerKey: ColumnHeader.marketRevenue,
  sortKey: SortKeys.Absolute,
  sortDir: SortDirectionType.desc ?? 'desc',
  label: SortLabels.Absolute,
};

export interface KpiOptions {
  label: string | ReactElement | Element;
  value: ColumnHeader;
}

export const BarChartKpiDisplayNames = {
  [ColumnHeader.checkbox]: '',
  [ColumnHeader.itemName]: 'Model',
  [ColumnHeader.marketRank]: 'Market Ranking',
  [ColumnHeader.marketRevenue]: 'Market Revenue',
  [ColumnHeader.marketShareRevenue]: 'Market Revenue Share',
  [ColumnHeader.marketVolume]: 'Market Units',
  [ColumnHeader.marketShareVolume]: 'Market Units Share',
  [ColumnHeader.marketPrice]: 'Market Price',
  [ColumnHeader.marketShare]: 'Market Share',
  [ColumnHeader.retailerRank]: 'Retailer Ranking',
  [ColumnHeader.retailerRevenue]: 'Retailer Revenue',
  [ColumnHeader.retailerShareRevenue]: 'Retailer Revenue Share',
  [ColumnHeader.retailerMixRevenue]: 'Retailer Revenue Mix',
  [ColumnHeader.retailerVolume]: 'Retailer Units',
  [ColumnHeader.retailerShareVolume]: 'Retailer Units Share',
  [ColumnHeader.retailerMixVolume]: 'Retailer Units Mix',
  [ColumnHeader.retailerPrice]: 'Retailer Price',
  [ColumnHeader.firstActivity]: 'First Activity',
  [ColumnHeader.feature]: 'Feature',
  [ColumnHeader.ranking]: 'Ranking',
  [ColumnHeader.brand]: 'Brand',
  [ColumnHeader.marketRevenueRank]: 'Market Ranking (Revenue)',
  [ColumnHeader.marketRevenueRankChange]: 'Market Ranking Change (Revenue)',
  [ColumnHeader.marketRevenueChange]: 'Market Revenue Change',
  [ColumnHeader.marketVolumeRank]: 'Market Ranking (Units)',
  [ColumnHeader.marketVolumeRankChange]: 'Market Ranking Change (Units)',
  [ColumnHeader.marketVolumeChange]: 'Market Units Change',
  [ColumnHeader.marketPriceChange]: 'Market Price Change',
  [ColumnHeader.retailerRevenueRank]: 'Retailer Ranking (Revenue)',
  [ColumnHeader.retailerRevenueRankChange]: 'Retailer Ranking Change (Revenue)',
  [ColumnHeader.retailerRevenueChange]: 'Retailer Revenue Change',
  [ColumnHeader.retailerVolumeRank]: 'Retailer Ranking (Units)',
  [ColumnHeader.retailerVolumeRankChange]: 'Retailer Ranking Change (Units)',
  [ColumnHeader.retailerVolumeChange]: 'Retailer Units Change',
  [ColumnHeader.retailerPriceChange]: 'Retailer Price Change',
  [ColumnHeader.marketExcludeRetailRevenue]: 'Market excl. Retail Revenue',
  [ColumnHeader.marketExcludeRetailVolume]: 'Market excl. Retailer Units',
  [ColumnHeader.marketExcludeRetailPrice]: 'Market excl. Retailer Price',
};

export const getBarChartKpiDisplayName = (headerKey: string): string => {
  return BarChartKpiDisplayNames[headerKey as ColumnHeader];
};

export interface OverviewKpi {
  marketTotal: number;
  retailerTotal: number;
  marketNew: number;
  retailerNew: number;
  restOfMarketRevenue: number;
  topItemsRevenuePercentage: number;
  items: LeaderboardKpi[];
  rollingSalesDto: ShareComparisonChartResp;
}

export enum OverviewKpiTitle {
  marketTotal = 'Total Products in the Market',
  retailerTotal = 'Total Selling in the Retailer',
  marketNew = 'New in the Market',
  retailerNew = 'New from Retailer',
  restOfMarketRevenue = 'Rest of market',
}

export const initialOverviewKpi: OverviewKpi = {
  marketTotal: 0,
  retailerTotal: 0,
  marketNew: 0,
  retailerNew: 0,
  restOfMarketRevenue: 0,
  topItemsRevenuePercentage: 0,
  items: [],
  rollingSalesDto: new ShareComparisonChartResp(),
};

export const initialTrendsKpi: TrendsKpi = {
  items: [],
};

export interface ItemFeaturesResp {
  itemId: string;
  features: Feature[];
}

export const defaultItemFeature = {
  itemId: '',
  features: [],
};

export interface TrendsKpi {
  items: LeaderboardKpi[];
}

export const BEST_SELLERS_KPI_OPTION: KpiTypesOption = {
  value: KpiOptionForValue.bestSellers,
  label: KpiOptionForLabel.bestSellers,
};

export const HIGHEST_GROWTH_KPI_OPTION: KpiTypesOption = {
  value: KpiOptionForValue.highestGrowth,
  label: KpiOptionForLabel.highestGrowth,
};

export enum RetailerKpiOption {
  absolute = 'absolute',
  share = 'share',
  mix = 'mix',
}

export const RetailerKpiOptionDisplayName = {
  [RetailerKpiOption.absolute]: 'Absolute',
  [RetailerKpiOption.share]: 'Share',
  [RetailerKpiOption.mix]: 'Mix',
};

/**
 * Enum for Component Name
 */
export enum ComponentName {
  PRODUCT_KPI_CARD_CONTAINER = 'Product Kpi Card',
  PRODUCT_TRENDS_MARKET_LINE_CHART = 'Products Market Line Chart',
  PRODUCT_TRENDS_RETAILER_LINE_CHART = 'Products Retailer Line Chart',
  PRODUCT_REVENUE_CONCENTRATION_TOP_5_PRODUCT = 'Revenue Concentration - top 5 products',
  PRODUCT_TRENDS_TOP_5_PRODUCTS = 'Top 5 products',
  PRODUCT_LEADERBOARD_TOP_SELLING_PRODUCTS = 'Top Selling Products',
  PRODUCT_LEADERBOARD_COMPARE_MODELS_TABLE = 'Compare Models',
  PRODUCT_LEADERBOARD_COMPARE_MODELS = 'Compare Models',
}

export interface ProductsCompareKpi {
  items: ProductsCompareKpiItem[];
  itemFeatures: ItemFeaturesResp[];
}

export interface ProductsCompareKpiItem {
  item: Item;
  marketRevenueRank: Rank;
  marketRevenue: Kpi;
  marketVolume: Kpi;
  marketVolumeRank: Rank;
  marketPrice: Kpi;
  marketShareRevenue: KpiPercentage;
  marketShareVolume: KpiPercentage;
  retailerRevenueRank: Rank;
  retailerRevenue: Kpi;
  retailerVolume: Kpi;
  retailerVolumeRank: Rank;
  retailerPrice: Kpi;
  retailerShareRevenue: KpiPercentage;
  retailerShareVolume: KpiPercentage;
  retailerMixRevenue: KpiPercentage;
  retailerMixVolume: KpiPercentage;
  rollingSalesDto?: ProductsLineChartResp;
  marketExcludeRetailRevenue: Kpi;
  marketExcludeRetailVolume: Kpi;
  marketExcludeRetailPrice: Kpi;
}

export enum ProductsCellClassType {
  TOTAL = 'Total',
  FEATURE = 'Feature',
}

export const initialProductsCompareKpi: ProductsCompareKpi = {
  items: [],
  itemFeatures: [],
};

export enum RankProperty {
  rank = 'rank',
  change = 'change',
  position = 'position',
}

export interface ProductsCompareExport {
  item: Item;
  itemContents: ProductsComparePeriodItem[];
}

export interface ProductsComparePeriodItem {
  period: string;
  marketRevenueRank: Rank;
  marketVolumeRank: Rank;
  marketRevenue: Kpi;
  marketVolume: Kpi;
  marketPrice: Kpi;
  marketShareRevenue: KpiPercentage;
  marketShareVolume: KpiPercentage;
  retailerRevenueRank: Rank;
  retailerVolumeRank: Rank;
  retailerRevenue: Kpi;
  retailerVolume: Kpi;
  retailerPrice: Kpi;
  retailerShareRevenue: KpiPercentage;
  retailerShareVolume: KpiPercentage;
  retailerMixRevenue: KpiPercentage;
  retailerMixVolume: KpiPercentage;
  marketExcludeRetailRevenue: Kpi;
  marketExcludeRetailVolume: Kpi;
  marketExcludeRetailPrice: Kpi;
}

export const Symbol = {
  PERCENTAGE: '(%)',
  PERCENTAGE_POINT: '(pp)',
};

export interface ProductAnalyzerGroupDetails {
  groupName: string;
  itemGroup: string[];
  sortOrder?: number;
  isPreferenceOwner?: boolean;
}

export interface EditGroupMode {
  groupDetails: ProductAnalyzerGroupDetails;
  isEditMode: boolean;
}

export interface ProductAnalyzerGroupKpi {
  groupDetails: ProductAnalyzerGroupDetails;
  groupKpis: LeaderboardKpi[];
}

export interface GroupState {
  groupName?: string;
  enableIsEditGroupMode?: () => void;
  isGroupingHeader?: boolean;
  isGroupEmpty?: boolean;
  isGroupingModelItem?: boolean;
  setIsGroupingRowExpanded?: (value: boolean) => void;
  onDeleteGroupMgs?: (itemGroup: string, itemName: string) => void;
  isPreferenceOwner?: boolean;

  groupSize?: number;
}
